/**
 * Campaign Gear — Pure logic for team-wide gear upgrades.
 * No DOM, no Three.js.
 */
import type { CampaignState, GearUpgradeState, WeaponKey, SharedGearKey, ItemGearKey, ItemKey } from '../types';
import { GEAR_UPGRADES, MAX_GEAR_TIER } from '../constants';

// =============================================================================
// DEFAULT STATE
// =============================================================================

export function createDefaultGearState(): GearUpgradeState {
  return {
    weaponTiers: {},
    sharedTiers: {},
    itemTiers: {},
  };
}

// =============================================================================
// PURCHASE
// =============================================================================

/**
 * Purchase the next tier of a gear upgrade.
 * Returns new CampaignState on success, null on failure.
 */
export function purchaseGearUpgrade(
  state: CampaignState,
  gearKey: string
): CampaignState | null {
  const def = GEAR_UPGRADES[gearKey];
  if (!def) return null;

  const gear = state.inventory.gearUpgrades;
  let currentTier: number;
  let category: 'weaponTiers' | 'sharedTiers' | 'itemTiers';

  if (def.category === 'weapon') {
    // Must own the weapon
    if (!state.inventory.ownedWeapons.has(gearKey as WeaponKey)) return null;
    currentTier = gear.weaponTiers[gearKey as WeaponKey] ?? 0;
    category = 'weaponTiers';
  } else if (def.category === 'shared') {
    currentTier = gear.sharedTiers[gearKey as SharedGearKey] ?? 0;
    category = 'sharedTiers';
  } else {
    currentTier = gear.itemTiers[gearKey as ItemGearKey] ?? 0;
    category = 'itemTiers';
  }

  if (currentTier >= MAX_GEAR_TIER) return null;

  const tierDef = def.tiers[currentTier];
  if (!tierDef) return null;
  if (state.playerMoney < tierDef.cost) return null;

  const newTiers = { ...gear[category], [gearKey]: currentTier + 1 };

  return {
    ...state,
    playerMoney: state.playerMoney - tierDef.cost,
    inventory: {
      ...state.inventory,
      gearUpgrades: {
        ...gear,
        [category]: newTiers,
      },
    },
  };
}

// =============================================================================
// ITEM UNLOCK GATE
// =============================================================================

/** Map from ItemKey to the gear key whose T1 unlocks purchasing that item. */
const ITEM_UNLOCK_MAP: Record<ItemKey, ItemGearKey> = {
  healSmall: 'healBoost',
  healLarge: 'healBoost',
};

/** Check whether an item is unlocked (requires T1+ of its gear upgrade). */
export function isItemUnlocked(gear: GearUpgradeState, item: ItemKey): boolean {
  const gearKey = ITEM_UNLOCK_MAP[item];
  if (!gearKey) return true; // no gate → always available
  return (gear.itemTiers[gearKey] ?? 0) >= 1;
}
